import store from "@/store/store";
import { Decimal } from 'decimal.js';
import {
  round2,
  roundMoney as roundMoneyEngine,
  roundToTwoDecimalPlaces as roundToTwoDecimalPlacesEngine,
  getModSum as getModSumEngine,
  getModifierEffectivePrice as getModifierEffectivePriceEngine,
  getItemSubtotal as getItemSubtotalEngine,
  getItemGrandTotal as getItemGrandTotalEngine,
  getOrderSubtotal as getOrderSubtotalEngine,
  getOrderGrandTotal as getOrderGrandTotalEngine,
} from "@/pricing";

Decimal.set({ precision: 20, rounding: 4 });

export const distributeAmount = (totalAmount, weights) => {
  if (weights.length === 0) return [];
  if (weights.every(w => w === 0)) return weights.map(() => 0);

  const totalDec = new Decimal(totalAmount);
  const totalWeight = weights.reduce((acc, val) => acc.plus(new Decimal(val)), new Decimal(0));

  if (totalWeight.isZero()) return weights.map(() => 0);

  let allocatedSum = new Decimal(0);

  const rawAllocations = weights.map(weight => {
    const w = new Decimal(weight);
    const share = totalDec.times(w).dividedBy(totalWeight);
    const rounded = share.toDecimalPlaces(2, Decimal.ROUND_FLOOR);
    allocatedSum = allocatedSum.plus(rounded);
    return {
      weight: w,
      value: rounded,
      remainder: share.minus(rounded)
    };
  });

  let remainder = totalDec.minus(allocatedSum);

  const sortedIndices = rawAllocations
      .map((item, index) => ({ index, remainder: item.remainder }))
      .sort((a, b) => b.remainder.cmp(a.remainder));

  const penny = new Decimal(0.01);
  let i = 0;
  while (remainder.greaterThan(0) && i < sortedIndices.length) {
    const idx = sortedIndices[i].index;
    rawAllocations[idx].value = rawAllocations[idx].value.plus(penny);
    remainder = remainder.minus(penny);
    i++;
  }

  return rawAllocations.map(item => item.value.toNumber());
};

// Delegates to the single pricing engine (src/pricing). Behaviour is identical
// to the previous inline Decimal HALF_UP implementation.
export const getItemSubtotal = (item) => getItemSubtotalEngine(item);

export const getItemGrandTotal = (item) => {
  const isTaxInclusive = store.getters["config/isTaxInclusive"];
  return getItemGrandTotalEngine(item, isTaxInclusive);
};

export const getInclusiveTax = (taxedPrice, taxRate) => {
  return setTaxRound(taxedPrice - (taxedPrice / (1 + taxRate)), 2);
};
export const getExclusiveTax = (price, taxRate) => {
  return setTaxRound((price * taxRate), 2);
};
export const getModSum = (item) => getModSumEngine(item);
export const getModifierEffectivePrice = (mod) => getModifierEffectivePriceEngine(mod);

export const calculateItemTaxTotal = (item) => {
  if (!item || !item.tax_items) return 0;
  let tax_sum = new Decimal(0);
  item.tax_items.forEach(function (tax_item) {
    tax_sum = tax_sum.plus(new Decimal(tax_item.tax_total));
  });
  return setTaxRound(tax_sum.toNumber());
};

export const getOrderSubTotal = (currentOrder) => getOrderSubtotalEngine(currentOrder);

// `products` is accepted for backwards compatibility but is unused.
export const getOrderGrantTotal = (currentOrder, products, isTaxInclusive) =>
  getOrderGrandTotalEngine(currentOrder, isTaxInclusive);

export const calculateOrderTax = (order) => {
  let overallTax = new Decimal(0);
  if (!order.items) return 0;

  order.items.forEach(item => {
    if (!item.void_status) {
      overallTax = overallTax.plus(new Decimal(item.tax || 0));
    }
  });

  return setTaxRound(overallTax.toNumber());
};

export const handlingRoundOffCalculations = (fn,item,refundQuantity=0, modPrice = 0,) => {
  // Validate input
  if (!item) {
    console.error('[handlingRoundOffCalculations] Invalid item', item);
    return 0;
  }

  // destructing with defaults
  const {
    discount = 0,
    wholeDiscount = 0,
    quantity = 0,
    price = 0,
    discountPropotion = 0
  } = item;

  // Validate critical values
  if (typeof price !== 'number' || isNaN(price)) {
    console.error('[handlingRoundOffCalculations] Invalid price', price, item);
    return 0;
  }
  if (typeof quantity !== 'number' || isNaN(quantity) || quantity === 0) {
    console.error('[handlingRoundOffCalculations] Invalid quantity', quantity, item);
    return 0;
  }

  // calculating new quantity regarding refunds
  let newQuantity = 0
  if(refundQuantity < 0 ){
    newQuantity = refundQuantity
  }
  else{
    newQuantity = quantity

  }


// creating modulus of quantity so that it is always positive regardless of the refunds
let iteratorQuantity = Math.abs(newQuantity)

  let wholeDiscountArray = []

  let subTotal = 0

  // Check if there's a distributed discount (discountPropotion) or whole order discount (wholeDiscount)
  // Use explicit number checks to avoid falsy 0 values being treated as no discount
  const hasDiscountPropotion = typeof discountPropotion === 'number' && discountPropotion !== 0;
  const hasWholeDiscount = typeof wholeDiscount === 'number' && wholeDiscount !== 0;

  if(hasDiscountPropotion || hasWholeDiscount){
      const effectiveDiscount = discountPropotion || wholeDiscount;

      // iterate over the quantity of the item
      for(let numberOfQuantity=1;numberOfQuantity <= iteratorQuantity;numberOfQuantity++){

        // if it is the last quantity i.e. if there are 3 quantities if it is on 3rd quantity then this clause will execute
            if(numberOfQuantity===iteratorQuantity){
              let previousDiscountTotal = 0
              if(wholeDiscountArray.length > 0){
                // calculate all the previous discounts on the other quantities e.g. if the array has [3.33,3.33] => return 6.66
                previousDiscountTotal = wholeDiscountArray.reduce((total,discount)=>total+discount, 0)
                // negate the remaning discounts from the propotion of the discount associated to the item e.g 10-6.66 => 3.34 and push
                wholeDiscountArray.push(roundToTwoDecimalPlaces(effectiveDiscount - previousDiscountTotal))
              }
              else{
                // add the distributed discount in the discount array if there is only one item
                wholeDiscountArray.push(roundToTwoDecimalPlaces(effectiveDiscount))
              }
            }
            else{
              // add the distributed discount in the discount array
              wholeDiscountArray.push(roundToTwoDecimalPlaces(effectiveDiscount))
            }
          }

    // calcualting subtotal
    wholeDiscountArray.forEach(calculatedDiscount=>{
      const itemDiscount = Math.abs(discount + calculatedDiscount);
      const itemPrice = modPrice >= 0 ? price - itemDiscount + modPrice : price - itemDiscount;
      subTotal += itemPrice;
    })
}

return fn(wholeDiscountArray,price,discount,newQuantity,subTotal,modPrice)

}

export const roundToTwo = (num) => round2(num);

function roundToTwoDecimalPlaces(num) {
  return roundToTwoDecimalPlacesEngine(num);
}

export const setTaxRound = (tax) => {
  const tempStore = store.getters["config/store"];
  return roundMoneyEngine(tax, tempStore && tempStore.tax_round);
};

export const calculateOrderLevelDistributedTax = (items, isTaxInclusive = false) => {
  // Convert items to use Decimal for precise calculations
  const itemsWithDecimal = items.map(item => {
    return {
      ...item,
      subtotal: new Decimal(String(item.subtotal || (item.price * item.quantity))),
      price: new Decimal(String(item.price)),
      quantity: new Decimal(String(item.quantity)),
      tax_rate: new Decimal(String(item.tax_rate))
    };
  });

  // Calculate raw tax for each item
  itemsWithDecimal.forEach(item => {
    if (isTaxInclusive) {
      // Inclusive: tax = subtotal - (subtotal / (1 + rate))
      item.raw_tax = item.subtotal.minus(item.subtotal.dividedBy(new Decimal('1').plus(item.tax_rate)));
    } else {
      // Exclusive: tax = subtotal * rate
      item.raw_tax = item.subtotal.times(item.tax_rate);
    }
  });

  // Calculate total raw tax amount
  const rawOrderTax = itemsWithDecimal.reduce(
    (sum, item) => sum.plus(item.raw_tax),
    new Decimal('0')
  );

  // Round the order level tax
  const roundedOrderTax = rawOrderTax.toDecimalPlaces(2, Decimal.ROUND_HALF_UP);

  // Allocate tax proportionally
  if (rawOrderTax.greaterThan(0)) {
    itemsWithDecimal.forEach(item => {
      // Calculate share of total tax
      const share = item.raw_tax.dividedBy(rawOrderTax);
      item.provisional_tax = roundedOrderTax.times(share);
      item.allocated_tax = item.provisional_tax.toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
    });

    // Handle rounding difference
    let allocatedTotal = itemsWithDecimal.reduce(
      (sum, item) => sum.plus(item.allocated_tax),
      new Decimal('0')
    );

    let roundingDifference = roundedOrderTax.minus(allocatedTotal);

    if (!roundingDifference.isZero()) {
      // Sort items by who is closest to rounding threshold
      const adjustments = [...itemsWithDecimal]
        .map((item, idx) => ({
          idx,
          diff: item.provisional_tax.minus(item.allocated_tax)
        }))
        .sort((a, b) => {
          if (roundingDifference.greaterThan(0)) {
            return b.diff.comparedTo(a.diff); // Reverse sort if adding
          } else {
            return a.diff.comparedTo(b.diff); // Regular sort if subtracting
          }
        });

      // Distribute rounding difference
      const adjustmentUnit = roundingDifference.greaterThan(0) ?
        new Decimal('0.01') : new Decimal('-0.01');

      while (!roundingDifference.isZero()) {
        const { idx } = adjustments.shift();
        itemsWithDecimal[idx].allocated_tax = itemsWithDecimal[idx].allocated_tax.plus(adjustmentUnit);
        roundingDifference = roundingDifference.minus(adjustmentUnit);
      }
    }
  } else {
    // If no tax, allocate 0 to all items
    itemsWithDecimal.forEach(item => {
      item.allocated_tax = new Decimal('0');
    });
  }

  // Convert back to regular numbers
  return {
    items: itemsWithDecimal.map(item => ({
      ...item,
      allocated_tax: parseFloat(item.allocated_tax.toFixed(2)),
      raw_tax: parseFloat(item.raw_tax.toFixed(4))
    })),
    total_tax: parseFloat(roundedOrderTax.toFixed(2))
  };
};
